import { useState, useEffect } from 'react';
import { CronScheduleMap, CronSchedulePreset, LeaderboardScheduleInfo } from '@repo/common-types';
import { Environment, environment } from '@repo/environment';
import StandardLayout from '@/common/layouts/StandardLayout';
import {
  Card,
  CardBody,
  CardHeader,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Row,
  Col,
  Alert,
  Spinner,
} from 'reactstrap';
import { format } from 'date-fns';
import {
  getLeaderboardScheduleRest,
  updateLeaderboardScheduleRest,
} from '@/services/leaderboards/leaderboards-service';
import { BsCheckCircle, BsExclamationCircle, BsInfoCircle, BsClock } from 'react-icons/bs';

/**
 * LeaderboardSchedulePage
 *
 * Admin page for managing the dynamic leaderboard schedule.
 * Allows admins to set when leaderboards end and automatically restart.
 */

// Group presets by category for organized dropdown display
const scheduleGroups = {
  'Minutes (for testing)': [
    CronSchedulePreset.EVERY_1_MINUTE,
    CronSchedulePreset.EVERY_3_MINUTES,
    CronSchedulePreset.EVERY_5_MINUTES,
    CronSchedulePreset.EVERY_10_MINUTES,
    CronSchedulePreset.EVERY_15_MINUTES,
    CronSchedulePreset.EVERY_20_MINUTES,
    CronSchedulePreset.EVERY_30_MINUTES,
  ],
  Hours: [
    CronSchedulePreset.EVERY_1_HOUR,
    CronSchedulePreset.EVERY_2_HOURS,
    CronSchedulePreset.EVERY_5_HOURS,
    CronSchedulePreset.EVERY_12_HOURS,
  ],
  Days: [CronSchedulePreset.EVERY_1_DAY, CronSchedulePreset.EVERY_7_DAYS],
  Default: [CronSchedulePreset.EVERY_WEEK_MONDAY_NOON],
};

const LeaderboardSchedulePage = () => {
  const [scheduleInfo, setScheduleInfo] = useState<LeaderboardScheduleInfo | null>(null);
  const [selectedCronString, setSelectedCronString] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Fetch the current schedule on mount
  useEffect(() => {
    const fetchSchedule = async () => {
      try {
        setIsLoading(true);
        const data = await getLeaderboardScheduleRest();
        setScheduleInfo(data);

        // Set the dropdown to show the current cron string
        if (data?.cronString) {
          setSelectedCronString(data.cronString);
        }
      } catch (error) {
        setErrorMessage('Failed to load schedule information');
        console.error('Error fetching schedule:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSchedule();
  }, []);

  const handleCronChange = (cronString: string) => {
    setSelectedCronString(cronString);
    setErrorMessage(null);
    setSuccessMessage(null);
  };

  // Check if form is dirty (schedule has changed)
  const isFormDirty = selectedCronString !== scheduleInfo?.cronString;

  // Disable in production environment
  const isProduction = environment === Environment.PRODUCTION;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setSuccessMessage(null);
    setIsSaving(true);

    try {
      if (!selectedCronString) {
        setErrorMessage('Please select a schedule');
        setIsSaving(false);
        return;
      }

      // Find the description from the preset
      const presetEntry = Object.entries(CronScheduleMap).find(([_, cron]) => cron === selectedCronString);
      const description = presetEntry
        ? CronSchedulePreset[presetEntry[0] as keyof typeof CronSchedulePreset]
        : selectedCronString;

      const updatedSchedule = await updateLeaderboardScheduleRest({
        cronString: selectedCronString,
        description,
      });

      setScheduleInfo(updatedSchedule);
      setSelectedCronString(updatedSchedule?.cronString || selectedCronString);
      setSuccessMessage(
        'Schedule updated successfully! All active leaderboards will now end at the next scheduled time.',
      );
    } catch (_) {
      setErrorMessage('Failed to update schedule');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <StandardLayout pageTitle="Leaderboard Schedule">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <Spinner color="primary" />
        </div>
      </StandardLayout>
    );
  }

  return (
    <StandardLayout pageTitle="Leaderboard Schedule">
      <div className="container-fluid py-4">
        <Row className="mb-4">
          <Col>
            <h2 className="mb-2">Leaderboard Schedule</h2>
            <p className="text-muted mb-0">Configure when leaderboards automatically end and restart</p>
          </Col>
        </Row>

        {/* Success Message */}
        {successMessage && (
          <Row className="mb-3">
            <Col>
              <Alert color="success" toggle={() => setSuccessMessage(null)}>
                <BsCheckCircle className="me-2" />
                {successMessage}
              </Alert>
            </Col>
          </Row>
        )}

        {/* Error Message */}
        {errorMessage && (
          <Row className="mb-3">
            <Col>
              <Alert color="danger" toggle={() => setErrorMessage(null)}>
                <BsExclamationCircle className="me-2" />
                {errorMessage}
              </Alert>
            </Col>
          </Row>
        )}

        <Row>
          <Col lg={8}>
            {/* Current Schedule Info Card */}
            {scheduleInfo && (
              <Card className="mb-4">
                <CardHeader>
                  <div className="d-flex align-items-center">
                    <BsClock className="me-2" />
                    <strong>Current Schedule</strong>
                  </div>
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col md={6} className="mb-3">
                      <Label className="text-muted small mb-1">Schedule</Label>
                      <div className="fw-bold fs-5">{scheduleInfo.description || scheduleInfo.cronString}</div>
                    </Col>
                    <Col md={6} className="mb-3">
                      <Label className="text-muted small mb-1">Next End Time</Label>
                      <div className="fw-bold">
                        {scheduleInfo.nextEndDate
                          ? format(new Date(scheduleInfo.nextEndDate), 'PPpp')
                          : 'Not scheduled'}
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Label className="text-muted small mb-1">Last End Time</Label>
                      <div className="fw-bold">
                        {scheduleInfo.previousEndDate
                          ? format(new Date(scheduleInfo.previousEndDate), 'PPpp')
                          : 'No previous data'}
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            )}

            {/* Update Schedule Form */}
            <Card>
              <CardHeader>
                <strong>Update Schedule</strong>
              </CardHeader>
              <CardBody>
                {isProduction && (
                  <Alert color="danger" className="mb-4">
                    <BsExclamationCircle className="me-2" />
                    <strong>Production Environment:</strong> Schedule changes are disabled in production for safety.
                  </Alert>
                )}
                <Alert color="warning" className="mb-4">
                  <BsExclamationCircle className="me-2" />
                  <strong>Warning:</strong> Changing the schedule will immediately update all active leaderboards.
                </Alert>

                <Form onSubmit={handleSubmit}>
                  <FormGroup>
                    <div className="fw-bold mb-2">
                      Next End:{' '}
                      {scheduleInfo?.nextEndDate ? format(new Date(scheduleInfo.nextEndDate), 'PPpp') : 'Not scheduled'}
                    </div>
                  </FormGroup>

                  <FormGroup>
                    <Input
                      type="select"
                      id="schedulePreset"
                      value={selectedCronString}
                      onChange={(e) => handleCronChange(e.target.value)}
                      className="form-select-lg"
                    >
                      {Object.entries(scheduleGroups).map(([groupLabel, presets]) => (
                        <optgroup label={groupLabel} key={groupLabel}>
                          {presets.map((preset) => (
                            <option key={preset} value={CronScheduleMap[preset]}>
                              {preset}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </Input>
                  </FormGroup>

                  <FormGroup>
                    <Input type="text" id="cronExpression" value={selectedCronString} disabled className="bg-light" />
                  </FormGroup>

                  <div className="d-flex justify-content-end gap-2">
                    <Button
                      color="primary"
                      type="submit"
                      size="lg"
                      disabled={isSaving || !selectedCronString || !isFormDirty || isProduction}
                    >
                      {isSaving ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Updating...
                        </>
                      ) : (
                        'Update Schedule'
                      )}
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>

          <Col lg={4}>
            {/* Info Card */}
            <Card>
              <CardHeader className="bg-light">
                <div className="d-flex align-items-center">
                  <BsInfoCircle className="me-2" />
                  <strong>About Schedules</strong>
                </div>
              </CardHeader>
              <CardBody>
                <p className="small mb-2">The schedule determines when leaderboards automatically end and restart.</p>
                <p className="small mb-2">
                  <strong>Testing schedules (minutes/hours):</strong> Use these for rapid testing and development.
                </p>
                <p className="small mb-0">
                  <strong>Production schedule:</strong> The default Monday noon UTC schedule is recommended for
                  production.
                </p>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </StandardLayout>
  );
};

export default LeaderboardSchedulePage;
